import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Form, Input, Select, message } from 'antd';
import { makePostCall, getUserDetails, setUserDetails } from '../../utils/helper';
import { useSites } from '../../hooks/useSites';
import './edit-profile.css';

const { Option } = Select;

const EditProfile = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { centers } = useSites();

  // Fetch user data when modal opens
  useEffect(() => {
    if (visible) {
      loadUserData();
    } else {
      // Clean up when modal closes
      form.resetFields();
      setIsSubmitting(false);
    }
  }, [visible, form]);

  const loadUserData = () => {
    // Get user data from localStorage (same as what's already available)
    const userDetails = getUserDetails();
    if (!userDetails) {
      message.error('User information not found');
      return;
    }

    form.setFieldsValue({
      user_firstname: userDetails.user_firstname,
      user_lastname: userDetails.user_lastname,
      username: userDetails.username,
      user_email: userDetails.user_email,
      user_mobile: userDetails.user_mobile,
      user_unit_name: userDetails.user_unit_name,
      user_designation: userDetails.user_designation,
      user_degree: userDetails.user_degree,
    });
  };

  const handleOk = useCallback(async () => {
    const userDetails = getUserDetails();
    if (!userDetails) {
      message.error('User data not loaded');
      return;
    }

    try {
      setIsSubmitting(true);

      // Validate all form fields first
      const values = await form.validateFields();

      // Compare current form values with original user data to detect changes
      const changedValues = {};

      // Check each form field against original data
      Object.keys(values).forEach(key => {
        const formValue = values[key];
        let originalValue = userDetails[key];

        // Detect actual changes (handle undefined/null/empty string cases)
        if (formValue !== originalValue && formValue !== '') {
          changedValues[key] = formValue;
        }
      });

      // Only submit if there are actual changes
      if (Object.keys(changedValues).length === 0) {
        message.info('No changes detected');
        onCancel();
        return;
      }

      // Sanitize input data (same as UserList component)
      const submitData = {
        ...changedValues,
        user_firstname: changedValues.user_firstname?.trim() || userDetails.user_firstname,
        user_lastname: changedValues.user_lastname?.trim() || userDetails.user_lastname,
        user_email: changedValues.user_email?.trim()?.toLowerCase() || userDetails.user_email,
        user_mobile: changedValues.user_mobile?.trim() || userDetails.user_mobile,
        user_designation: changedValues.user_designation?.trim() || userDetails.user_designation,
        user_degree: changedValues.user_degree?.trim() || userDetails.user_degree,
        existing_user_id: userDetails.user_id,
        user_id: getUserDetails().username, // This is required by the backend
      };

      // Submit to backend using the same endpoint as EditUser
      const response = await makePostCall('/add-user', submitData);

      if (response.data?.success) {
        message.success('Profile updated successfully');

        // Fetch fresh user data from backend to sync localStorage
        try {
          const userListResponse = await makePostCall('/user-list', {});
          const allUsers = userListResponse.data?.data || [];

          // Find the current user in the list
          const updatedUser = allUsers.find(u => u.user_id === userDetails.user_id);

          if (updatedUser) {
            // Prepare user details object matching the expected format
            const freshUserDetails = {
              user_id: updatedUser.user_id,
              username: updatedUser.username,
              user_firstname: updatedUser.user_firstname || updatedUser.first_name,
              user_lastname: updatedUser.user_lastname || updatedUser.last_name,
              user_fullname: updatedUser.user_fullname || `${updatedUser.user_firstname || updatedUser.first_name} ${updatedUser.user_lastname || updatedUser.last_name || ''}`,
              user_email: updatedUser.user_email,
              user_mobile: updatedUser.user_mobile,
              user_type: updatedUser.user_type,
              user_unit_name: updatedUser.user_unit_name,
              user_designation: updatedUser.user_designation,
              user_degree: updatedUser.user_degree,
              user_signature: updatedUser.user_signature,
              user_permissions: updatedUser.user_permissions || []
            };

            // Update localStorage using the helper function
            setUserDetails(freshUserDetails);
          }
        } catch (error) {
          console.error('Error fetching updated user data:', error);
          // Still close the modal even if refresh fails
        }

        onCancel();

        if (onSuccess) {
          onSuccess();
        }
      } else {
        message.error(response.data?.message || 'Failed to update profile');
      }
    } catch (errorInfo) {
      if (errorInfo.errorFields) {
        message.error('Please fix the validation errors in the form');
      } else {
        console.error('Update failed:', errorInfo);
        message.error('Failed to update profile. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [form, onCancel, onSuccess]);

  const unitOptions = useMemo(() => centers, [centers]);

  return (
    <Modal
      open={visible}
      title="Edit Profile"
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
      width={700}
      className="edit-profile-modal"
      confirmLoading={isSubmitting}
      okText={isSubmitting ? "Saving..." : "Save Changes"}
      cancelButtonProps={{ disabled: isSubmitting }}
    >
      <Form
        layout="vertical"
        form={form}
        className="edit-profile-modal__form"
      >
        {/* Personal Information */}
        <div className="form-section-title">Personal Information</div>
        <div className="edit-profile-modal__form-row">
          <Form.Item
            className="edit-profile-modal__form-item"
            label="First Name"
            name="user_firstname"
            rules={[
              {
                pattern: /^[a-zA-Z\s]+$/,
                message: "First name can only contain letters and spaces!",
              },
              {
                min: 2,
                message: "First name must be at least 2 characters long!",
              },
            ]}
          >
            <Input placeholder="Enter first name" disabled />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Last Name"
            name="user_lastname"
            rules={[
              {
                pattern: /^[a-zA-Z\s]*$/,
                message: "Last name can only contain letters and spaces!",
              },
            ]}
          >
            <Input placeholder="Enter last name" disabled />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Username"
            name="username"
            rules={[
              {
                min: 3,
                message: "Username must be at least 3 characters long!",
              },
              {
                pattern: /^[a-zA-Z0-9_.]+$/,
                message: "Username can only contain letters, numbers, underscores, and dots!",
              },
            ]}
          >
            <Input placeholder="Enter username" disabled />
          </Form.Item>
        </div>

        {/* Contact Information */}
        <div className="form-section-title">Contact Information</div>
        <div className="edit-profile-modal__form-row">
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Email"
            name="user_email"
            rules={[
              {
                type: "email",
                message: "Please enter a valid email address!",
              },
            ]}
          >
            <Input type="email" placeholder="Enter email address" disabled />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Mobile"
            name="user_mobile"
            rules={[
              {
                pattern: /^[6-9]\d{9}$/,
                message: "Please enter a valid 10-digit mobile number starting with 6-9!",
              },
            ]}
          >
            <Input placeholder="Enter mobile number" maxLength={10} />
          </Form.Item>
        </div>

        {/* Professional Details */}
        <div className="form-section-title">Professional Details</div>
        <div className="edit-profile-modal__form-row">
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Location"
            name="user_unit_name"
          >
            <Select placeholder="Select Unit" options={unitOptions} disabled />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Designation"
            name="user_designation"
          >
            <Input placeholder="Enter designation" />
          </Form.Item>
          <Form.Item
            className="edit-profile-modal__form-item"
            label="Degree"
            name="user_degree"
          >
            <Input placeholder="Enter degree" />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default EditProfile;
